import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatNumber, formatTaka } from '@/utils/number';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpAccountsCountsQueryOptions } from '../../api/pcmpAccounts.api';
import { pcmpRequestedCountsQueryOptions } from '../../api/pcmpRequested.api';
import {
  PCMP_ACCOUNTS_TAB_COUNT,
  PCMP_ACCOUNTS_TAB_LABELS,
} from '../../constants/pcmpAccounts.constants';
import { PCMP_ACCOUNTS_RETURN_REQUESTS_TAB } from '../../constants/pcmpRequest.constants';
import { usePcmpAccountsFilters } from '../../hooks/usePcmpAccountsFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { PCMP_ACCOUNTS_VIEWS } from '../../types/pcmpAccounts.types';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { PcmpQueueTabs } from '../PcmpQueue/PcmpQueueTabs';
import { PcmpRequestedListView } from '../PcmpRequested/PcmpRequestedListView';
import { PcmpAccountsBatchesView } from './PcmpAccountsBatchesView';
import { PcmpAccountsItemsView } from './PcmpAccountsItemsView';
import { PcmpRedisburseBatchesView } from './PcmpRedisburseBatchesView';

export function PcmpAccountsQueueTable() {
  const controller = usePcmpAccountsFilters();
  const { tab, source, appliedFilters, pageParams } = controller;
  const { can } = usePcmpPermissions();
  // Accounts staff process, approve and reject Accounts Payment Return requests (LDS-805).
  const canDecideReturns = can(PCMP_PERMISSION.ACCOUNTS, PCMP_PERMISSION.REQUEST_ACT);
  const tabs = useMemo(
    () => PCMP_ACCOUNTS_VIEWS.filter((view) => view !== 'RETURN_REQUESTS' || canDecideReturns),
    [canDecideReturns],
  );

  const { data: counts } = useQuery(pcmpAccountsCountsQueryOptions(appliedFilters));
  const { data: requestedCounts } = useQuery({
    ...pcmpRequestedCountsQueryOptions(appliedFilters),
    enabled: canDecideReturns,
  });
  const returnRequestsTab = requestedCounts?.tabs.find(
    (entry) => entry.key === PCMP_ACCOUNTS_RETURN_REQUESTS_TAB,
  );

  const returnRequestsParams = useMemo(
    () => ({ ...pageParams, tab: PCMP_ACCOUNTS_RETURN_REQUESTS_TAB }),
    [pageParams],
  );

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Accounts Disbursement</h1>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {counts
            ? `${formatNumber(counts.pendingBatches)} batches awaiting disbursement · ${formatNumber(
                counts.disbursementFailed,
              )} failed (${formatTaka(counts.disbursementFailedAmount)})`
            : ' '}
        </p>
      </div>

      <PcmpQueueTabs
        tabs={tabs}
        labels={PCMP_ACCOUNTS_TAB_LABELS}
        value={tab}
        countOf={
          counts
            ? (value) =>
                value === 'RETURN_REQUESTS'
                  ? (returnRequestsTab?.count ?? 0)
                  : PCMP_ACCOUNTS_TAB_COUNT[value](counts)
            : undefined
        }
        onChange={controller.setTab}
      />

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-accounts" showPayoutFilters />

      {source.kind === 'batches' ? (
        <PcmpAccountsBatchesView
          controller={controller}
          batchKind={source.batchKind}
          view={source.view}
        />
      ) : source.kind === 'redisburse' ? (
        <PcmpRedisburseBatchesView controller={controller} view={source.view} />
      ) : source.kind === 'returnRequests' ? (
        <PcmpRequestedListView
          controller={controller}
          params={returnRequestsParams}
          tabInfo={returnRequestsTab}
        />
      ) : (
        <PcmpAccountsItemsView controller={controller} tab={source.tab} />
      )}
    </div>
  );
}
